import React from "react";
import AccordionSection from "../../../../Components/Common/AccordionSection/index";
import { LayerSettingsInput } from "../../../../Components/Common/Form/Form";
import Icon from "../../../../Components/Common/Icon/index";
import { Download, Save, AlertCircle } from "react-feather";
import { Loader } from "../../../../Components/Loader/index";
import { getDownloadLink } from "../../../../api/storageApi";
import { postCustomTask } from "../../../../api/caseApi";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";

class GeodataDownload extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectAll: false,
      isProcessing: false,
      isError: false,
      currentRequestId: null,
    };

    this.downloadFile = this.downloadFile.bind(this);
    this.onToggleSelectAll = this.onToggleSelectAll.bind(this);
    this.handleContainerChange = this.handleContainerChange.bind(this);
    this.onSaveSelected = this.onSaveSelected.bind(this);
    this.containerRef = React.createRef();
    this.isSyncing = false;
  }

  async downloadFile(file) {
    const url = await getDownloadLink(file);
    const link = document.createElement("a");
    link.download = "download";
    link.href = url;
    link.click();
  }

  componentDidMount() {
    if (this.containerRef.current) {
      this.containerRef.current.addEventListener(
        "change",
        this.handleContainerChange,
        true
      );
    }
  }

  componentWillUnmount() {
    if (this.containerRef.current) {
      this.containerRef.current.removeEventListener(
        "change",
        this.handleContainerChange,
        true
      );
    }
  }

  componentDidUpdate(prevProps) {
    // Check if notification is for our task type and matches current requestId
    if (this.props.osmNotification && this.state.currentRequestId) {
      const notification = this.props.osmNotification;

      // Only process notifications for multiResultsGeotiffDownloader tasks
      if (
        notification.type === "multiResultsGeotiffDownloader" &&
        notification.requestId === this.state.currentRequestId
      ) {
        // Status 2 = finished successfully
        if (
          notification.status === 2 &&
          prevProps.osmNotification?.status !== 2
        ) {
          this.setState({
            isProcessing: false,
            isError: false,
          });
        }
        // Status 3 = error
        else if (
          prevProps.osmNotification?.status !== 3 &&
          notification.status === 3
        ) {
          this.setState({
            isError: true,
            isProcessing: false,
          });
        }
        // Status 0 or 1 = processing (initializing or running)
        else if (
          (notification.status === 0 || notification.status === 1) &&
          !this.state.isProcessing
        ) {
          this.setState({
            isProcessing: true,
            isError: false,
          });
        }
      }
    }
  }

  handleContainerChange() {
    if (!this.containerRef.current) return;
    if (this.isSyncing) return;
    const checkboxes = this.containerRef.current.querySelectorAll(
      'input[type="checkbox"]'
    );
    const bottomCheckbox = this.bottomSelectAllRef;
    const boxesToConsider = Array.from(checkboxes).filter(
      (cb) => cb !== bottomCheckbox
    );
    const allChecked =
      boxesToConsider.length > 0 && boxesToConsider.every((cb) => cb.checked);
    if (this.state.selectAll !== allChecked) {
      this.setState({ selectAll: allChecked });
    }
  }

  onToggleSelectAll(e) {
    const checked = e.target.checked;
    this.setState({ selectAll: checked });
    if (!this.containerRef.current) return;
    const checkboxes = this.containerRef.current.querySelectorAll(
      'input[type="checkbox"]'
    );
    this.isSyncing = true;
    checkboxes.forEach((cb) => {
      if (cb === this.bottomSelectAllRef) return;
      cb.checked = checked;
      const event = new Event("change", { bubbles: true });
      cb.dispatchEvent(event);
    });
    this.isSyncing = false;
    // Also update header checkboxes for empty accordions via a global event
    const globalEvent = new CustomEvent("geodata-select-all", {
      detail: { checked },
    });
    window.dispatchEvent(globalEvent);
  }

  async onSaveSelected() {
    if (!this.containerRef.current) return;
    const selected = Array.from(
      this.containerRef.current.querySelectorAll(
        'input[type="checkbox"]:checked'
      )
    )
      .filter((cb) => cb !== this.bottomSelectAllRef)
      .map((cb) => ({
        name: cb.getAttribute("data-name"),
        url: cb.getAttribute("data-url"),
      }))
      .filter((x) => x.name || x.url);

    if (selected.length === 0) {
      console.log("No files selected");
      return;
    }

    if (!this.props.currentCase?.id) {
      console.error("Case ID not available");
      return;
    }

    try {
      this.setState({
        isProcessing: true,
        isError: false,
        currentRequestId: null,
      });

      const taskRequest = {
        caseId: this.props.currentCase.id,
        type: "multiResultsGeotiffDownloader",
        request: {
          files: selected,
        },
      };

      const requestId = await postCustomTask(taskRequest);
      console.log("Custom task requested:", requestId);

      // Store requestId to track this specific task
      this.setState({
        currentRequestId: requestId,
      });
    } catch (error) {
      console.error("Error requesting custom task:", error);
      this.setState({
        isProcessing: false,
        isError: true,
      });
    }
  }

  render() {
    const { t } = this.props;

    return (
      <div className="layer-settings-container" ref={this.containerRef}>
        <h3
          className="raster-settings-header"
          style={{ marginBottom: "24px" }}
        >{`${t("DownloadGeodata")}`}</h3>
        {this.props.resultsJsonOrigin.pages.map((x) => {
          if (x.name === "Report" || x.name === "CreatePage") {
            return null;
          }
          return (
            <AccordionSection
              title={t(x.name)}
              buildingGroup
              style={{ width: "100%" }}
              geodataDownload
            >
              {x.lyrs.map((y) => {
                if (y.type === "wind_dir" || y.type === "wind_stream") {
                  return (
                    <>
                      <div
                        className="layer-settings-input-wrapper"
                        onClick={() => this.downloadFile(y.geo_u_utm)}
                      >
                        <LayerSettingsInput
                          name={"geodata"}
                          value={t(y.name) + " u_utm"}
                          style={{
                            width: "280px",
                            height: "27px",
                            marginRight: "12px",
                            border: "0px solid black",
                            backgroundColor: "transparent",
                            cursor: "pointer",
                            letterSpacing: "0.2px",
                          }}
                          readOnly
                        />
                        <input
                          type="checkbox"
                          style={{ width: "12px" }}
                          data-name={`${t(y.name)} u_utm`}
                          data-url={y.geo_u_utm || y.geo_u || y.geo_utm}
                        />
                        <button
                          className="tree-form-btn"
                          style={{
                            marginRight: "3px",
                            backgroundColor: "transparent",
                            cursor: "pointer",
                          }}
                        >
                          <Icon className="download-icon" icon={Download} />
                        </button>
                      </div>
                      <div
                        className="layer-settings-input-wrapper"
                        onClick={() => this.downloadFile(y.geo_v_utm)}
                      >
                        <LayerSettingsInput
                          name={"geodata"}
                          value={t(y.name) + " v_utm"}
                          style={{
                            width: "280px",
                            height: "27px",
                            marginRight: "12px",
                            border: "0px solid black",
                            backgroundColor: "transparent",
                            cursor: "pointer",
                            letterSpacing: "0.2px",
                          }}
                          readOnly
                        />
                        <input
                          type="checkbox"
                          style={{ width: "12px" }}
                          data-name={`${t(y.name)} v_utm`}
                          data-url={y.geo_v_utm || y.geo_v || y.geo_utm}
                        />
                        <button
                          className="tree-form-btn"
                          style={{
                            marginRight: "3px",
                            backgroundColor: "transparent",
                            cursor: "pointer",
                          }}
                        >
                          <Icon className="download-icon" icon={Download} />
                        </button>
                      </div>
                    </>
                  );
                }
                return (
                  <div className="layer-settings-input-wrapper">
                    <LayerSettingsInput
                      name={"geodata"}
                      value={t(y.name)}
                      style={{
                        width: "280px",
                        height: "27px",
                        marginRight: "12px",
                        border: "0px solid black",
                        backgroundColor: "transparent",
                        cursor: "pointer",
                        letterSpacing: "0.2px",
                      }}
                      readOnly
                    />
                    <input
                      type="checkbox"
                      style={{ width: "12px" }}
                      data-name={`${t(y.name)}`}
                      data-url={y.geo_utm}
                    />
                    <button
                      className="tree-form-btn"
                      style={{
                        marginRight: "3px",
                        backgroundColor: "transparent",
                        cursor: "pointer",
                      }}
                      onClick={() => this.downloadFile(y.geo_utm)}
                    >
                      <Icon className="download-icon" icon={Download} />
                    </button>
                  </div>
                );
              })}
            </AccordionSection>
          );
        })}
        <div
          style={{
            marginTop: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            color: "#444",
            fontSize: "12px",
            fontFamily: "Segoe UI",
            fontWeight: "600",
          }}
        >
          <button
            className="btn-modal-cancel"
            onClick={this.onSaveSelected}
            disabled={this.state.isProcessing}
            style={{
              display: "flex",
              alignItems: "center",
              color: "#444",
              marginRight: "0px",
              padding: "5px 6px",
              minWidth: "110px",
              justifyContent: "space-around",
              opacity: this.state.isProcessing ? 0.6 : 1,
              cursor: this.state.isProcessing ? "not-allowed" : "pointer",
            }}
          >
            <div style={{ display: "flex" }}>
              <Save size={"14px"} strokeWidth={"1.8px"} />
            </div>
            {t("Save selected") || "Save selected"}
          </button>
          <div style={{ marginRight: "21px" }}>
            <span style={{ marginRight: "6px" }}>
              {t("Select all") || "Select all"}
            </span>
            <input
              type="checkbox"
              style={{ width: "12px" }}
              ref={(el) => (this.bottomSelectAllRef = el)}
              checked={this.state.selectAll}
              onChange={this.onToggleSelectAll}
            />
          </div>
        </div>
        {this.state.isProcessing && (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "left",
                marginLeft: -63,
              }}
            >
              <Loader size="small-osm" />
            </div>
            <p style={{ fontSize: 11, cursor: "default", color: "gray" }}>
              {t("Processing")}...
            </p>
          </>
        )}

        {!this.state.isProcessing && this.state.isError && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginTop: "12px",
            }}
          >
            <AlertCircle size="12px" color="#ac4141" />
            <p
              className="new-layer-name-error"
            >
              {t("ErrorProcessingFiles") ||
                "An error occurred while processing files. Please try again."}
            </p>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    resultsJsonOrigin: state.results.resultsJsonOrigin,
    currentCase: state.projects.currentCase,
    osmNotification: state.tools.osmNotification,
  };
};

export default withTranslation()(
  connect(mapStateToProps, null)(GeodataDownload)
);
